import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Tab, Image, Table, Spinner, Alert, Pagination } from 'react-bootstrap';
import { api } from '../services/api';
import { formatDateTimeRu } from '../utils/dateFormat';
import { formatSubjectName } from '../utils/formatSubjectName';
import { getActionTypeLabel } from './ActionTypesModal';

/** Минимальные данные для открытия модалки — модалка грузит полные данные по id из API */
export interface TzConfirmedProduct {
  id: number;
  article?: string;
  new_wave?: boolean;
}

/** Полная строка таблицы «Подготовлено ТЗ по артикулам» (данные из API статистики) */
export interface TzConfirmedTableRow extends TzConfirmedProduct {
  name?: string;
  thumb_url?: string | null;
  thumb_middle_url?: string | null;
  desc?: string;
  eng_name?: string;
  eng_desc?: string;
  season?: string | null;
  subject_name?: string;
  image_url_1?: string | null;
  image_url_2?: string | null;
  image_url_3?: string | null;
  image_url_4?: string | null;
  package_summary?: string;
}

function getFullImageUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  const u = url.trim();
  if (u.startsWith('http')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (u.startsWith('/')) return u;
  return u;
}

function formatValue(val: any): string {
  if (val == null) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

interface TzProductDetailModalProps {
  show: boolean;
  onHide: () => void;
  product: TzConfirmedProduct | null;
}

/** Данные товара из GET /products/:id/tz-detail */
type FullProduct = {
  id: string;
  name?: string;
  article?: string;
  subject_name?: string;
  url?: string;
  thumb_url?: string;
  thumb_middle_url?: string;
  desc?: string;
  eng_name?: string;
  eng_desc?: string;
  season?: string;
  package_summary?: string;
  image_url_1?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  required_price?: number | null;
  order_quantity?: string | null;
  cert_id?: string | null;
  target_price_cny?: number | null;
  package_length?: number | null;
  package_width?: number | null;
  package_height?: number | null;
  commission_fbo?: number | null;
  purchase_percent?: number | null;
};

const TzProductDetailModal: React.FC<TzProductDetailModalProps> = ({ show, onHide, product }) => {
  const productId = product ? String(product.id) : '';
  const [fullProduct, setFullProduct] = useState<FullProduct | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  const [historyTabActive, setHistoryTabActive] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const historyLimit = 20;

  useEffect(() => {
    if (!show || !productId) return;
    setFullProduct(null);
    setProductError(null);
    setHistoryData([]);
    setHistoryPage(1);
    setHistoryTabActive(false);
  }, [show, productId]);

  useEffect(() => {
    if (!show || !productId) return;
    setProductLoading(true);
    setProductError(null);
    api.getProductTzDetail(productId)
      .then((res: any) => {
        const data = res?.data?.data ?? res?.data;
        if (data) setFullProduct(data);
        else setProductError('Товар не найден');
      })
      .catch((e: any) => {
        setProductError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
        setFullProduct(null);
      })
      .finally(() => setProductLoading(false));
  }, [show, productId]);

  useEffect(() => {
    if (!productId || !historyTabActive) return;
    setHistoryLoading(true);
    setHistoryError(null);
    api.getProductAuditLog(productId, historyPage, historyLimit)
      .then((res) => {
        setHistoryData(res.data);
        setHistoryTotal(res.total);
        setHistoryTotalPages(res.totalPages);
      })
      .catch((e: any) => {
        setHistoryError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
        setHistoryData([]);
      })
      .finally(() => setHistoryLoading(false));
  }, [productId, historyPage, historyTabActive]);

  const imageUrls = React.useMemo(() => {
    if (!fullProduct) return [];
    const urls: string[] = [];
    const thumb = fullProduct.thumb_middle_url || fullProduct.thumb_url;
    if (thumb) urls.push(getFullImageUrl(thumb));
    for (const key of ['image_url_1', 'image_url_2', 'image_url_3', 'image_url_4'] as const) {
      const u = fullProduct[key];
      if (u && !urls.includes(getFullImageUrl(u))) urls.push(getFullImageUrl(u));
    }
    if (urls.length === 0 && fullProduct.image_url_1) urls.push(getFullImageUrl(fullProduct.image_url_1));
    return urls;
  }, [fullProduct]);

  if (!product) return null;

  const titleLabel = fullProduct ? (fullProduct.name || fullProduct.article || fullProduct.id) : `ID ${productId}`;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Подготовлено ТЗ — {titleLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="product" id="tz-product-tabs" onSelect={(k) => k === 'history' && setHistoryTabActive(true)}>
          <Tab eventKey="product" title="Товар">
            {productLoading && (
              <div className="text-center py-4 d-flex flex-column align-items-center gap-2">
                <Spinner animation="border" />
                <span className="text-muted">Загрузка данных товара…</span>
              </div>
            )}
            {productError && !productLoading && (
              <Alert variant="danger" dismissible onClose={() => setProductError(null)}>{productError}</Alert>
            )}
            {!productLoading && !productError && fullProduct && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <Table responsive variant="dark" className="table-dark" size="sm">
                    {product.new_wave ? (
                      /* Заголовки как в NewWave */
                      <>
                        <thead>
                          <tr>
                            <th>img</th>
                            <th>SKU</th>
                            <th>Название</th>
                            <th>Предмет WB</th>
                            <th>URL товара</th>
                            <th>Изображение</th>
                            <th>Нужная цена на wb</th>
                            <th>Сертификат</th>
                            <th>Заказ, количество</th>
                            <th>Длина упак. тары, см</th>
                            <th>Ширина упак. тары, см</th>
                            <th>Высота упак. тары, см</th>
                            <th>%FBO</th>
                            <th>%Выкуп</th>
                            <th>Себестоимость, руб.</th>
                            <th>Итог цены в юанях</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {imageUrls.length > 0 ? (
                                <Image src={imageUrls[0]} alt="" width={50} height={50} className="rounded" style={{ objectFit: 'cover' }} referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              ) : (
                                <div className="bg-secondary rounded d-flex align-items-center justify-content-center text-muted" style={{ width: 50, height: 50 }}>—</div>
                              )}
                            </td>
                            <td>
                              <a href={`https://www.wildberries.ru/catalog/${fullProduct.id}/detail.aspx`} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">{fullProduct.id}</a>
                            </td>
                            <td style={{ minWidth: 220 }} className="text-break">{fullProduct.name ?? '—'}</td>
                            <td>{formatSubjectName(fullProduct.subject_name)}</td>
                            <td>{fullProduct.url ? <a href={fullProduct.url} target="_blank" rel="noopener noreferrer" className="text-primary small">URL</a> : '—'}</td>
                            <td>
                              {imageUrls.length > 0 ? (
                                <div className="d-flex gap-1 flex-wrap">
                                  {imageUrls.slice(0, 4).map((url, i) => (
                                    <Image key={i} src={url} alt="" style={{ maxHeight: 40, width: 'auto' }} referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                  ))}
                                </div>
                              ) : '—'}
                            </td>
                            <td>{fullProduct.required_price != null ? fullProduct.required_price : '—'}</td>
                            <td>{fullProduct.cert_id ?? '—'}</td>
                            <td>{fullProduct.order_quantity ?? '—'}</td>
                            <td>{fullProduct.package_length != null ? fullProduct.package_length : '—'}</td>
                            <td>{fullProduct.package_width != null ? fullProduct.package_width : '—'}</td>
                            <td>{fullProduct.package_height != null ? fullProduct.package_height : '—'}</td>
                            <td>{fullProduct.commission_fbo != null ? fullProduct.commission_fbo : '—'}</td>
                            <td>{fullProduct.purchase_percent != null ? fullProduct.purchase_percent : '—'}</td>
                            <td>—</td>
                            <td>{fullProduct.target_price_cny != null ? fullProduct.target_price_cny : '—'}</td>
                          </tr>
                        </tbody>
                      </>
                    ) : (
                      /* Заголовки как в NewProducts */
                      <>
                        <thead>
                          <tr>
                            <th>img</th>
                            <th>SKU</th>
                            <th>Артикул</th>
                            <th>Название</th>
                            <th>Предмет WB</th>
                            <th>Нужная цена на wb</th>
                            <th>Сертификат</th>
                            <th>Заказ, количество</th>
                            <th>Длина упак. тары, см</th>
                            <th>Ширина упак. тары, см</th>
                            <th>Высота упак. тары, см</th>
                            <th>%FBO</th>
                            <th>%Выкуп</th>
                            <th>Себестоимость, руб.</th>
                            <th>Итог цены в юанях</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {imageUrls.length > 0 ? (
                                <Image src={imageUrls[0]} alt="" width={50} height={50} className="rounded" style={{ objectFit: 'cover' }} referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              ) : (
                                <div className="bg-secondary rounded d-flex align-items-center justify-content-center text-muted" style={{ width: 50, height: 50 }}>—</div>
                              )}
                            </td>
                            <td>
                              <a href={`https://www.wildberries.ru/catalog/${fullProduct.id}/detail.aspx`} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">{fullProduct.id}</a>
                            </td>
                            <td>{fullProduct.article ?? '—'}</td>
                            <td style={{ minWidth: 220 }} className="text-break">{fullProduct.name ?? '—'}</td>
                            <td>{formatSubjectName(fullProduct.subject_name)}</td>
                            <td>{fullProduct.required_price != null ? fullProduct.required_price : '—'}</td>
                            <td>{fullProduct.cert_id ?? '—'}</td>
                            <td>{fullProduct.order_quantity ?? '—'}</td>
                            <td>{fullProduct.package_length != null ? fullProduct.package_length : '—'}</td>
                            <td>{fullProduct.package_width != null ? fullProduct.package_width : '—'}</td>
                            <td>{fullProduct.package_height != null ? fullProduct.package_height : '—'}</td>
                            <td>{fullProduct.commission_fbo != null ? fullProduct.commission_fbo : '—'}</td>
                            <td>{fullProduct.purchase_percent != null ? fullProduct.purchase_percent : '—'}</td>
                            <td>—</td>
                            <td>{fullProduct.target_price_cny != null ? fullProduct.target_price_cny : '—'}</td>
                          </tr>
                        </tbody>
                      </>
                    )}
                  </Table>
                </div>
                <div className="mt-3 small">
                  {fullProduct.id === productId && (fullProduct.package_summary?.trim() || fullProduct.season) && (
                    <div className="mb-2 text-muted">
                      {fullProduct.package_summary?.trim() && <span><strong>Упаковка:</strong> {fullProduct.package_summary}</span>}
                      {fullProduct.package_summary?.trim() && fullProduct.season && ' · '}
                      {fullProduct.season && <span><strong>Сезонность:</strong> {fullProduct.season}</span>}
                    </div>
                  )}
                  {fullProduct.id === productId && fullProduct.desc != null && fullProduct.desc !== '' && (
                    <div className="mb-2">
                      <strong>Описание:</strong>
                      <div className="text-break mt-1" style={{ whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>{fullProduct.desc}</div>
                    </div>
                  )}
                  {fullProduct.id === productId && (fullProduct.eng_desc || fullProduct.eng_name) && (
                    <div>
                      <strong>Описание (EN):</strong>
                      <div className="text-break mt-1" style={{ whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>{fullProduct.eng_desc ?? fullProduct.eng_name ?? '—'}</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </Tab>
          <Tab eventKey="history" title="История">
            {historyError && (
              <Alert variant="danger" dismissible onClose={() => setHistoryError(null)}>{historyError}</Alert>
            )}
            {historyLoading && (
              <div className="text-center py-4 d-flex flex-column align-items-center gap-2">
                <Spinner animation="border" />
                <span className="text-muted">Загрузка истории…</span>
              </div>
            )}
            {!historyLoading && historyData.length === 0 && !historyError && (
              <p className="text-muted mb-0">Нет записей.</p>
            )}
            {!historyLoading && historyData.length > 0 && (
              <>
                <Table responsive size="sm" variant="dark">
                  <thead>
                    <tr>
                      <th>Дата и время</th>
                      <th>Действие</th>
                      <th>Было</th>
                      <th>Стало</th>
                      <th>Пользователь</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDateTimeRu(row.created_at)}</td>
                        <td>{getActionTypeLabel(row.action_type)}{row.field_name ? ` (${row.field_name})` : ''}</td>
                        <td className="text-break" style={{ maxWidth: 120 }}>{formatValue(row.old_value)}</td>
                        <td className="text-break" style={{ maxWidth: 120 }}>{formatValue(row.new_value)}</td>
                        <td>{row.user_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {historyTotalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="small text-muted">Всего: {historyTotal}</span>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev disabled={historyPage <= 1} onClick={() => setHistoryPage((p) => Math.max(1, p - 1))} />
                      <Pagination.Item active>{historyPage} / {historyTotalPages}</Pagination.Item>
                      <Pagination.Next disabled={historyPage >= historyTotalPages} onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))} />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default TzProductDetailModal;
